import type { Order } from '@/domain/Order';
import type { ParcelTerminal } from '@/domain/Parcel';

interface OrderUserInfoCardProps {
  order: Order;
  showLotNumber?: boolean;
}

function getAddressCountryDisplay(
  country: string | { country?: string } | undefined,
): string {
  if (country == null) return '';
  if (typeof country === 'object' && 'country' in country)
    return country.country ?? '';
  return String(country);
}

function formatTerminalAddress(terminal: ParcelTerminal): string {
  return (
    [terminal.name, terminal.address, terminal.city, terminal.postalCode]
      .filter(Boolean)
      .join(', ') || '-'
  );
}

export function OrderUserInfoCard({
  order,
  showLotNumber = false,
}: OrderUserInfoCardProps) {
  const user = order.user;
  const address =
    Object.keys(order.address).length > 0 ? order.address : user?.address;
  const assignedDoctors =
    user?.practitioners
      ?.map((doctor) => `${doctor.name} ${doctor.surname}`)
      .join(', ') ||
    (order.doctor ? `${order.doctor.name} ${order.doctor.surname}` : '') ||
    '-';
  const batchNumber =
    order.batch_number == null || order.batch_number === ''
      ? '-'
      : String(order.batch_number);
  const lotNumber =
    order.lot_number == null || order.lot_number === ''
      ? '-'
      : String(order.lot_number);

  const formatAddress = (): string => {
    if (!address) return '-';
    if (typeof address === 'object' && Object.keys(address).length === 0)
      return '-';
    if (!('city' in address)) return '-';

    // Parcel-terminal delivery: show the terminal address instead of the
    // customer's street address. Home delivery leaves terminal null.
    if (address.terminal) return formatTerminalAddress(address.terminal);

    const countryStr = getAddressCountryDisplay(address.country);
    const parts = [
      [address.line1, address.line2].filter(Boolean).join(' '),
      address.city,
      countryStr,
      address.zipcode,
    ].filter(Boolean);
    return parts.join(', ') || '-';
  };

  return (
    <div className="bg-background space-y-2 rounded-lg p-6">
      <p className="text-table-foreground text-base font-normal">
        {user?.name} {user?.surname}
      </p>
      <div className="flex flex-col gap-y-1 text-sm">
        <div className="flex min-h-[1.25rem] items-start gap-x-10">
          <span className="text-table-foreground w-40 shrink-0 text-base leading-5">
            Address
          </span>
          <span className="text-table-foreground min-w-0 flex-1 text-base leading-5 whitespace-pre-line">
            {formatAddress()}
          </span>
        </div>
        <div className="flex min-h-[1.25rem] items-center gap-x-10">
          <span className="text-table-foreground w-40 shrink-0 text-base leading-5">
            Email
          </span>
          <span className="text-table-foreground text-base leading-5">
            {user?.email || '-'}
          </span>
        </div>
        <div className="flex min-h-[1.25rem] items-center gap-x-10">
          <span className="text-table-foreground w-40 shrink-0 text-base leading-5">
            Phone
          </span>
          <span className="text-table-foreground text-base leading-5">
            {user?.phone || '-'}
          </span>
        </div>
        <div className="flex min-h-[1.25rem] items-center gap-x-10">
          <span className="text-table-foreground w-40 shrink-0 text-base leading-5">
            Subscription period
          </span>
          <span className="text-table-foreground text-base leading-5">
            {order.period} month(s)
          </span>
        </div>
        <div className="flex min-h-[1.25rem] items-start gap-x-10">
          <span className="text-table-foreground w-40 shrink-0 text-base leading-5">
            Assigned doctor
          </span>
          <span className="text-table-foreground min-w-0 flex-1 text-base leading-5 whitespace-pre-line">
            {assignedDoctors}
          </span>
        </div>
        <div className="flex min-h-[1.25rem] items-center gap-x-10">
          <span className="text-table-foreground w-40 shrink-0 text-base leading-5">
            Batch number
          </span>
          <span className="text-table-foreground text-base leading-5">
            {batchNumber}
          </span>
        </div>
        {showLotNumber && (
          <div className="flex min-h-[1.25rem] items-center gap-x-10">
            <span className="text-table-foreground w-40 shrink-0 text-base leading-5">
              LOT number
            </span>
            <span className="text-table-foreground text-base leading-5">
              {lotNumber}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
